import { Injectable, inject } from '@angular/core';
import {
  CanActivate,
  Router,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { Observable } from 'rxjs';
import { UnauthorizedAccessService } from '../services/unauthorized-access.service';
import { AuthService } from '../services/auth.service';
import { environment } from '../../../environments/environment';

/**
 * Professional Profile Guard
 * 
 * Verifica che l'utente professional/collaborator abbia un professional_id valido
 * salvato in sessionStorage prima di accedere alle route protette.
 * 
 * IMPORTANTE:
 * - Questo controllo si applica SOLO a utenti con ruolo 'professional' o 'collaborator'
 * - Gli altri ruoli (admin, backoffice_*) possono accedere senza professional_id
 *   perché sono in modalità gestione/backoffice
 * 
 * IMPORTANTE SICUREZZA:
 * - Questo è un controllo CLIENT-SIDE (può essere bypassato facilmente)
 * - sessionStorage può essere modificato manualmente via DevTools
 * - Il BACKEND DEVE SEMPRE validare che il professional_id appartiene
 *   all'utente Azure AD autenticato tramite il token JWT
 * - NON fidarsi MAI solo dei controlli frontend
 * 
 * Usage:
 * ```typescript
 * {
 *   path: 'axa/professionals-registration-progress',
 *   component: ProfessionalsProgressionComponent,
 *   canActivate: [AuthGuard, RoleGuard, ProfessionalProfileGuard],
 *   data: { roles: ['professional', 'collaborator'] }
 * }
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class ProfessionalProfileGuard implements CanActivate {
  private router = inject(Router);
  private unauthorizedService = inject(UnauthorizedAccessService);
  private authService = inject(AuthService); // ← Usa AuthService per validare dal JWT

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    // Skip in development if guards are disabled
    const disableGuards = (environment as any).disableAuthGuards;
    if (disableGuards && !environment.production) {
      console.log('🔓 ProfessionalProfileGuard bypassed (development only)');
      return true;
    }

    // ✅ SECURE: Get current user from JWT token (re-parsed)
    // DO NOT trust localStorage['user'] - it can be manipulated!
    const user = this.authService.getCurrentUser();
    
    if (!user) {
      console.log('❌ ProfessionalProfileGuard: No user (invalid token)');
      this.unauthorizedService.setFromGuard(true);
      return this.router.createUrlTree(['/unauthorized']);
    }

    const userRoles = user.roles || [];
    
    // Check if user is professional or collaborator
    const isProfessionalOrCollaborator = userRoles.includes('professional') || userRoles.includes('collaborator');
    
    if (!isProfessionalOrCollaborator) {
      // User is backoffice (admin, backoffice_*, etc.) - allow access without professional_id check
      console.log('✅ ProfessionalProfileGuard: Backoffice user, skipping professional_id check');
      return true;
    }
    
    // User is professional/collaborator - check professional_id
    // First check if it's in the JWT token (most secure)
    if (user.professionalId) {
      console.log('✅ ProfessionalProfileGuard: Professional ID from JWT token:', user.professionalId);
      return true;
    }
    
    // Fallback: check sessionStorage (less secure, but needed during transition period)
    const professionalId = sessionStorage.getItem('professional_id');
    
    if (!professionalId || !professionalId.trim()) {
      console.log('❌ ProfessionalProfileGuard: Professional/Collaborator without professional_id');
      console.log('⚠️ User tried to access professional route without completing profile check');
      
      // Mark that redirect is from guard
      this.unauthorizedService.setFromGuard(true);
      
      // Redirect to unauthorized page
      return this.router.createUrlTree(['/unauthorized']);
    }

    console.log('✅ ProfessionalProfileGuard: Professional ID verified (from sessionStorage):', professionalId);
    return true;
  }
}
